import { sessionFromClaims } from './claims';
import { createDiscovery, IdpDiscovery } from './discovery';
import { postForm } from './http';
import { createJwksResolver, JwksResolver } from './jwks';
import { verifyIdToken } from './jwt';
import { IdpAuthError, IdpProvider, IdpSession, IdpTokens } from './provider';

/**
 * O provider de verdade: Authorization Code + PKCE S256 contra o realm
 * `seazone`, ativo com `REACT_APP_AUTH_PROVIDER=keycloak`.
 *
 * So o issuer e configurado: authorize, token, JWKS e logout saem todos do
 * discovery. O client `sapron-frontend` e **publico** — nao existe
 * `client_secret` em lugar nenhum daqui, e o PKCE e quem prova que quem troca
 * o code e quem comecou o login.
 */

export const CLIENT_ID_PADRAO = 'sapron-frontend';

/** `openid` e obrigatorio para vir `id_token`; o resto e nome e e-mail. */
const SCOPE = 'openid profile email';

const MS_POR_SEGUNDO = 1000;

interface TokenResponse {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly id_token?: string;
  readonly expires_in?: number;
}

export interface KeycloakIdpProviderOptions {
  readonly issuer: string;
  readonly clientId?: string;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
  /** Ganchos de teste: sem eles, discovery e JWKS saem do issuer de verdade. */
  readonly discovery?: () => Promise<IdpDiscovery>;
  readonly getKey?: JwksResolver;
}

function tokensFrom(response: TokenResponse, now: () => number, anterior?: string): IdpTokens {
  if (response.access_token === undefined || response.expires_in === undefined) {
    throw new IdpAuthError(
      'token_response_incompleta',
      'IDP respondeu sem access_token/expires_in.',
    );
  }
  // O Keycloak rotaciona o refresh token. Usar o antigo depois da rotacao e o
  // jeito de perder a sessao no meio do dia, entao o novo tem precedencia e o
  // anterior so sobra quando a resposta veio sem nenhum.
  const refreshToken = response.refresh_token ?? anterior;

  return {
    accessToken: response.access_token,
    ...(refreshToken !== undefined && { refreshToken }),
    ...(response.id_token !== undefined && { idToken: response.id_token }),
    expiresAt: Math.floor(now() / MS_POR_SEGUNDO) + response.expires_in,
  };
}

export function createKeycloakIdpProvider(options: KeycloakIdpProviderOptions): IdpProvider {
  const {
    issuer,
    clientId = CLIENT_ID_PADRAO,
    fetchImpl,
    now = () => Date.now(),
    getKey,
  } = options;

  const discovery =
    options.discovery ?? createDiscovery({ issuer, ...(fetchImpl && { fetchImpl }) });

  const resolveKey =
    getKey ??
    createJwksResolver({
      jwksUri: async () => (await discovery()).jwksUri,
      ...(fetchImpl && { fetchImpl }),
    });

  async function claimsDoIdToken(tokens: IdpTokens, nonce?: string) {
    if (tokens.idToken === undefined) {
      throw new IdpAuthError('id_token_ausente', 'IDP respondeu sem id_token.');
    }
    return verifyIdToken(tokens.idToken, {
      issuer,
      clientId,
      getKey: resolveKey,
      now,
      ...(nonce !== undefined && { nonce }),
    });
  }

  return {
    async authorizeUrl({ state, nonce, codeChallenge, redirectUri, silent }) {
      const { authorizationEndpoint } = await discovery();
      const url = new URL(authorizationEndpoint);
      url.searchParams.set('response_type', 'code');
      url.searchParams.set('client_id', clientId);
      url.searchParams.set('redirect_uri', redirectUri);
      url.searchParams.set('scope', SCOPE);
      url.searchParams.set('state', state);
      url.searchParams.set('nonce', nonce);
      url.searchParams.set('code_challenge', codeChallenge);
      url.searchParams.set('code_challenge_method', 'S256');
      if (silent === true) url.searchParams.set('prompt', 'none');
      return url.toString();
    },

    async exchangeCode({ code, codeVerifier, redirectUri }) {
      const { tokenEndpoint } = await discovery();
      const response = await postForm<TokenResponse>(
        tokenEndpoint,
        {
          grant_type: 'authorization_code',
          code,
          redirect_uri: redirectUri,
          client_id: clientId,
          code_verifier: codeVerifier,
        },
        { ...(fetchImpl && { fetchImpl }) },
      );
      return tokensFrom(response, now);
    },

    async refresh(refreshToken) {
      const { tokenEndpoint } = await discovery();
      const response = await postForm<TokenResponse>(
        tokenEndpoint,
        { grant_type: 'refresh_token', refresh_token: refreshToken, client_id: clientId },
        { ...(fetchImpl && { fetchImpl }) },
      );
      return tokensFrom(response, now, refreshToken);
    },

    async toSession(tokens, sessionOptions): Promise<IdpSession> {
      return sessionFromClaims(await claimsDoIdToken(tokens, sessionOptions?.nonce));
    },

    async endSession({ idToken, postLogoutRedirectUri }) {
      const { endSessionEndpoint } = await discovery();
      if (endSessionEndpoint === undefined) return undefined;
      // Params, e nao URL pronta: quem chama faz POST. Com `id_token_hint` na
      // query o token iria parar no historico do navegador e no log do IDP —
      // a proibicao de "token na URL" vale tambem para a saida.
      return {
        url: endSessionEndpoint,
        params: {
          client_id: clientId,
          post_logout_redirect_uri: postLogoutRedirectUri,
          ...(idToken !== undefined && { id_token_hint: idToken }),
        },
      };
    },
  };
}
